import {OperationVariables} from '@apollo/client';
import React from 'react';

import {
  QueryResultType,
  SkippableBenchlingQueryResult,
  SuccessReturnType,
} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import FieldError from 'ui-library/FieldError';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import BaseSubdependencyList, {
  LoadingBaseSubdependencyList,
  SubdependencyOption,
} from './BaseSubdependencyList';
import {ChildDependencyTypes} from './ChildDependencyTypes';

// All dependency types with subdependencies are schemas except for dropdowns and workflow tasks.
const DEFAULT_PARENT_DEPENDENCY_TYPE = 'Schema';
interface Schema {
  allFieldsJson: JSONScalar;
  __typename?: string;
}
interface BaseProps<TData, TVariables extends OperationVariables> extends Omit<
  ChildDependencyTypes,
  'parentDependencySelectedId'
> {
  result: SkippableBenchlingQueryResult<TData, TVariables>;
  getSchemaFromResult: (data: SuccessReturnType<TData>) => Schema | InaccessibleInfoGraphQL | null;
  fieldName?: string;
  isWorkflowOutput?: boolean;
}

export default function BaseSchemaSubdependencyInput<
  TData,
  TVariables extends OperationVariables = OperationVariables,
>({
  result,
  getSchemaFromResult,
  subdependencies,
  onChange,
  dependencyValidation,
  updateDependencyConfigValidation,
  disabled,
  fieldName = 'field',
  parentdependencyType = DEFAULT_PARENT_DEPENDENCY_TYPE,
  isWorkflowOutput,
}: BaseProps<TData, TVariables>): JSX.Element | null {
  if (result.type === QueryResultType.ERROR || result.type === QueryResultType.SKIPPED) {
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return (
      <LoadingBaseSubdependencyList
        subdependencies={subdependencies}
        subdependencyType={fieldName}
        parentdependencyType={DEFAULT_PARENT_DEPENDENCY_TYPE}
      />
    );
  }
  const schema = getSchemaFromResult(result);
  if (schema == null) {
    return <></>;
  }
  if (isInaccessible(schema)) {
    return <FieldError message="Selected schema is inaccessible." />;
  }
  const subdependencyOptions: Array<SubdependencyOption> = schema.allFieldsJson.map((field: SchemaField) => ({
    id: field.apiId,
    name: field.name,
    fieldConstraints: {
      internalType: field.fieldType,
      isMulti: field.isMulti,
      isRequired: field.isRequired,
    },
    field,
  }));
  return (
    <BaseSubdependencyList
      subdependencies={subdependencies}
      onChange={onChange}
      dependencyValidation={dependencyValidation}
      updateDependencyConfigValidation={updateDependencyConfigValidation}
      disabled={disabled}
      isLoading={false}
      subdependencyOptions={subdependencyOptions}
      subdependencyType={fieldName}
      parentdependencyType={parentdependencyType}
      isWorkflowOutput={isWorkflowOutput ?? false}
    />
  );
}
